import React, { useCallback, useEffect, useState } from 'react';
import { FiAlertTriangle, FiCheck, FiGlobe, FiLock, FiPlus, FiRefreshCw } from 'react-icons/fi';
import { addAllowedDomains, getAllowedDomains, type AddAllowedDomainsResult, type WorkforceAllowedDomains } from '../claudeagents/claudeAgentApi';

/** One line summing up a save: what reached the environment and how many agents changed. */
function describeResult(result: AddAllowedDomainsResult): string {
    const parts: string[] = [];
    const env = result.environment;
    if (env.added?.length)
        parts.push(`Added ${env.added.join(', ')} to the environment`);
    if (env.already_present?.length)
        parts.push(`Already on the environment: ${env.already_present.join(', ')}`);
    if (env.skipped)
        parts.push(`Environment not changed (${env.skipped})`);
    const { updated, skipped, failed } = result.agents;
    parts.push(`${updated} agent${updated === 1 ? '' : 's'} updated`);
    if (skipped)
        parts.push(`${skipped} already allowed or not eligible`);
    if (failed)
        parts.push(`${failed} could not be updated`);
    return `${parts.join('. ')}.`;
}

/**
 * Profile > Allowed Domains — the websites the user's Digital Workforce may open
 * with web fetch. Adding a domain allows it on the environment and on every
 * Claude agent the user owns, so all of them can reach it.
 */
const AllowedDomainsTab: React.FC = () => {
    const [data, setData] = useState<WorkforceAllowedDomains | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [failures, setFailures] = useState<string[]>([]);
    const [input, setInput] = useState('');
    const [includeSubdomains, setIncludeSubdomains] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        return getAllowedDomains()
            .then(setData)
            .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load allowed domains'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const hasPending = input.trim().length > 0;

    const handleSave = useCallback(async () => {
        const pending = input.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
        if (!pending.length)
            return;
        setSaving(true);
        setError(null);
        setNotice(null);
        setFailures([]);
        try {
            const result = await addAllowedDomains(pending, includeSubdomains);
            setNotice(describeResult(result));
            setFailures(result.agents.details.failed.map(a => `${a.name}: ${a.error || 'not updated'}`));
            setInput('');
            await load();
        }
        catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to save allowed domains');
        }
        finally {
            setSaving(false);
        }
    }, [input, includeSubdomains, load]);

    const env = data?.environment;
    const envHosts = env?.allowed_hosts ?? [];
    const envLimited = env?.networking === 'limited';
    const agents = data?.agents ?? [];

    return (<>
      <div className="profile-form__header profile-page__docs-header">
        <div className="profile-form__header-icon"><FiGlobe size={16}/></div>
        <div className="allowed-domains__header-text">
          <div className="profile-form__title">Allowed Domains</div>
          <div className="profile-form__desc">Websites your Digital Workforce agents may open with web fetch</div>
        </div>
        <button type="button" className="allowed-domains__reload" onClick={() => { void load(); }} disabled={loading} title="Reload" aria-label="Reload allowed domains">
          <FiRefreshCw size={13} className={loading ? 'allowed-domains__spin' : undefined}/>
        </button>
      </div>

      {error && (<div className="allowed-domains__msg allowed-domains__msg--error" role="alert">
          <FiAlertTriangle size={13}/> {error}
        </div>)}

      <section className="allowed-domains__section">
        <div className="allowed-domains__section-title">Add domains</div>
        <p className="allowed-domains__hint">
          Each domain is allowed for every one of your Claude agents. Hostnames only (no https:// or paths); separate several with commas or spaces.
        </p>
        <div className="allowed-domains__add">
          <input type="text" className="glass-input profile-field__input allowed-domains__input" placeholder="e.g. docs.example.com" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') {
                e.preventDefault();
                void handleSave();
            }
        }} disabled={saving} aria-label="Domains to allow"/>
          <button type="button" className="glass-btn profile-page__upload-btn allowed-domains__save" disabled={saving || !hasPending} onClick={() => { void handleSave(); }}>
            {saving ? <><span className="profile-loader profile-loader--sm"/> Saving…</> : <><FiPlus size={13}/> Add</>}
          </button>
        </div>
        <label className="allowed-domains__check">
          <input type="checkbox" checked={includeSubdomains} onChange={e => setIncludeSubdomains(e.target.checked)} disabled={saving}/>
          Also allow subdomains on the environment (adds *.domain)
        </label>
        {notice && (<div className="allowed-domains__msg allowed-domains__msg--ok">
            <FiCheck size={13}/> {notice}
          </div>)}
        {failures.length > 0 && (<ul className="allowed-domains__failures">
            {failures.map(f => <li key={f}>{f}</li>)}
          </ul>)}
      </section>

      <section className="allowed-domains__section">
        <div className="allowed-domains__section-title">
          <FiLock size={12}/> Environment hosts
          {env && envLimited && <span className="agency-docs__panel-count">{envHosts.length} / {env.allowed_hosts_max}</span>}
        </div>
        {loading && !data ? (<div className="allowed-domains__hint">Loading…</div>) : !env?.environment_id ? (<div className="allowed-domains__hint">No environment is configured for this account.</div>) : !envLimited ? (<div className="allowed-domains__hint">Environment networking is unrestricted, so only each agent's own list applies.</div>) : envHosts.length === 0 ? (<div className="allowed-domains__hint">No hosts yet.</div>) : (<div className="allowed-domains__chips">
            {envHosts.map(h => <span key={h} className="allowed-domains__chip">{h}</span>)}
          </div>)}
      </section>

      <section className="allowed-domains__section">
        <div className="allowed-domains__section-title">
          Agents
          {data && <span className="agency-docs__panel-count">{agents.length}</span>}
        </div>
        {loading && !data ? (<div className="allowed-domains__hint">Loading…</div>) : agents.length === 0 ? (<div className="allowed-domains__hint">No Claude agents yet.</div>) : (<ul className="allowed-domains__agents">
            {agents.map(a => {
                const domains = a.allowed_domains ?? [];
                const status = a.error
                    ? `Could not be read: ${a.error}`
                    : a.has_toolset === false
                        ? 'No built-in tools, so web fetch is not available'
                        : a.blocked_domains?.length
                            ? `Uses a blocked-domains list (${a.blocked_domains.join(', ')})`
                            : a.web_fetch_enabled === false
                                ? 'Web fetch is turned off for this agent'
                                : domains.length === 0
                                    ? 'No agent-level restriction'
                                    : null;
                return (<li key={a.agent_id} className="allowed-domains__agent">
                  <div className="allowed-domains__agent-head">
                    <span className="allowed-domains__agent-name">{a.name}</span>
                    {domains.length > 0 && <span className="agency-docs__panel-count">{domains.length} / {data?.allowed_domains_max ?? 64}</span>}
                  </div>
                  {status && <div className={`allowed-domains__hint${a.error ? ' allowed-domains__hint--warn' : ''}`}>{status}</div>}
                  {domains.length > 0 && (<div className="allowed-domains__chips">
                      {domains.map(d => <span key={d} className="allowed-domains__chip">{d}</span>)}
                    </div>)}
                </li>);
            })}
          </ul>)}
      </section>
    </>);
};

export default AllowedDomainsTab;
